/**
 * RGIE / AREI rule pack (Belgium, Livre 1 — domestic installations).
 * Rule IDs match docs/requirements/rgie.md. Rules marked `verified: false` come from
 * secondary sources and must be confirmed against the AREI text and with inspectors; the UI
 * shows them as such.
 */
import type { Id } from '@elec/domain';
import { isCountedPoint, mostSensitiveRcd, type CircuitInfo } from '@elec/analysis';
import {
  byId,
  DEDICATED,
  ids,
  isWet,
  MAX_RATING_A,
  name,
  NO_EARTH,
  rcdType,
  rule,
  sensitivityMa,
  type Rule,
} from './core.ts';
import { COMMON } from './common.ts';

const BE = byId([
  rule(
    {
      id: 'R-RCD-00',
      severity: 'error',
      title: {
        fr: 'Circuit sans protection différentielle',
        en: 'Circuit without RCD',
        nl: 'Kring zonder differentieel',
      },
      why: {
        fr: 'Toute installation domestique doit être protégée par un dispositif différentiel.',
        en: 'Every domestic installation must be protected by a residual-current device.',
        nl: 'Elke huishoudelijke installatie moet beveiligd zijn door een differentieelschakelaar.',
      },
      fix: {
        fr: 'Raccordez le circuit en aval d’un interrupteur différentiel.',
        en: 'Connect the circuit downstream of an RCD.',
        nl: 'Sluit de kring aan stroomafwaarts van een differentieel.',
      },
      verified: true,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => c.rcds.length === 0)
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : aucun différentiel en amont`,
            en: `Circuit ${name(c)}: no RCD upstream`,
            nl: `Kring ${name(c)}: geen differentieel stroomopwaarts`,
          },
        })),
  ),
  rule(
    {
      id: 'R-RCD-01',
      severity: 'error',
      title: {
        fr: 'Différentiel général > 300 mA',
        en: 'Main RCD above 300 mA',
        nl: 'Hoofddifferentieel boven 300 mA',
      },
      why: {
        fr: "À l'origine de l'installation, un différentiel de 300 mA au plus est requis.",
        en: 'At the origin of the installation, an RCD of at most 300 mA is required.',
        nl: 'Aan de oorsprong van de installatie is een differentieel van hoogstens 300 mA vereist.',
      },
      fix: {
        fr: 'Utilisez un différentiel général de 300 mA (type A).',
        en: 'Use a 300 mA main RCD (type A).',
        nl: 'Gebruik een hoofddifferentieel van 300 mA (type A).',
      },
      verified: false,
    },
    ({ a }) => {
      const out = new Map<Id, CircuitInfo>();
      for (const c of a.circuits) {
        const origin = c.rcds.at(-1);
        if (origin && sensitivityMa(origin) > 300) out.set(origin.id, c);
      }
      return [...out].map(([id, c]) => {
        const r = c.rcds.at(-1)!;
        return {
          targets: [id],
          sheetId: c.sheet.id,
          message: {
            fr: `${r.tag} : ${r.props.sensitivityMa} mA à l'origine (max. 300 mA)`,
            en: `${r.tag}: ${r.props.sensitivityMa} mA at the origin (max. 300 mA)`,
            nl: `${r.tag}: ${r.props.sensitivityMa} mA aan de oorsprong (max. 300 mA)`,
          },
        };
      });
    },
  ),
  rule(
    {
      id: 'R-RCD-02',
      severity: 'error',
      title: {
        fr: 'Pièce humide sans différentiel 30 mA',
        en: 'Wet location without 30 mA RCD',
        nl: 'Vochtige ruimte zonder differentieel 30 mA',
      },
      why: {
        fr: 'Les circuits des salles de bain, buanderies, lave-linge, sèche-linge et lave-vaisselle doivent être protégés par un différentiel de 30 mA au plus (art. 86).',
        en: 'Circuits for bathrooms, laundries, washing machines, dryers and dishwashers need an RCD of at most 30 mA (art. 86).',
        nl: 'Kringen voor badkamers, wasplaatsen, wasmachines, droogkasten en vaatwassers vereisen een differentieel van hoogstens 30 mA (art. 86).',
      },
      fix: {
        fr: 'Déplacez le circuit sous un différentiel 30 mA.',
        en: 'Move the circuit under a 30 mA RCD.',
        nl: 'Plaats de kring onder een differentieel van 30 mA.',
      },
      verified: false,
    },
    (ctx) =>
      ctx.a.circuits.flatMap((c) => {
        const why = isWet(ctx, c);
        const s = mostSensitiveRcd(c);
        if (!why || (s !== undefined && s <= 30)) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} (${why.fr}) : différentiel ${s ?? '—'} mA, 30 mA requis`,
              en: `Circuit ${name(c)} (${why.en}): RCD ${s ?? '—'} mA, 30 mA required`,
              nl: `Kring ${name(c)} (${why.nl}): differentieel ${s ?? '—'} mA, 30 mA vereist`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-RCD-03',
      severity: 'warning',
      title: { fr: 'Différentiel de type AC', en: 'Type AC RCD', nl: 'Differentieel type AC' },
      why: {
        fr: 'Le type AC n’est plus admis pour les nouvelles installations et modifications importantes depuis le 1er juin 2023.',
        en: 'Type AC is no longer allowed for new installations and major modifications since 1 June 2023.',
        nl: 'Type AC is sinds 1 juni 2023 niet meer toegelaten voor nieuwe installaties en belangrijke wijzigingen.',
      },
      fix: {
        fr: 'Remplacez-le par un type A (ou F/B selon les charges).',
        en: 'Replace it with type A (or F/B depending on the loads).',
        nl: 'Vervang hem door type A (of F/B naargelang de belasting).',
      },
      verified: false,
    },
    ({ project }) =>
      Object.values(project.elements)
        .filter(
          (e) => (e.symbolId.startsWith('iec.rcd') || e.symbolId === 'iec.rcbo') && e.props.rcdType === 'AC',
        )
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: { fr: `${e.tag} : type AC`, en: `${e.tag}: type AC`, nl: `${e.tag}: type AC` },
        })),
  ),
  rule(
    {
      id: 'R-RCD-04',
      severity: 'warning',
      title: {
        fr: 'Trop de circuits sous un différentiel 30 mA',
        en: 'Too many circuits under a 30 mA RCD',
        nl: 'Te veel kringen onder een differentieel 30 mA',
      },
      why: {
        fr: 'Au plus 8 circuits par différentiel 30 mA, pour limiter les déclenchements intempestifs.',
        en: 'At most 8 circuits per 30 mA RCD, to limit nuisance tripping.',
        nl: 'Hoogstens 8 kringen per differentieel van 30 mA, om ongewenst uitschakelen te beperken.',
      },
      fix: {
        fr: 'Ajoutez un second différentiel 30 mA et répartissez les circuits.',
        en: 'Add a second 30 mA RCD and split the circuits.',
        nl: 'Voeg een tweede differentieel van 30 mA toe en verdeel de kringen.',
      },
      verified: false,
    },
    ({ a, project }) =>
      [...a.byRcd]
        .filter(
          ([id, list]) =>
            !!project.elements[id] &&
            sensitivityMa(project.elements[id]!) <= 30 &&
            list.filter((c) => c.rcds[0]?.id === id).length > 8,
        )
        .map(([id, list]) => ({
          targets: [id],
          message: {
            fr: `${project.elements[id]!.tag} : ${list.length} circuits (max. 8)`,
            en: `${project.elements[id]!.tag}: ${list.length} circuits (max. 8)`,
            nl: `${project.elements[id]!.tag}: ${list.length} kringen (max. 8)`,
          },
        })),
  ),
  rule(
    {
      id: 'R-RCD-05',
      severity: 'error',
      title: {
        fr: 'Borne de recharge sans son propre différentiel 30 mA',
        en: 'EV charger without its own 30 mA RCD',
        nl: 'Laadpaal zonder eigen differentieel van 30 mA',
      },
      why: {
        fr: 'Chaque point de charge a son circuit et son propre différentiel de 30 mA au plus : type B, ou type A avec détection des courants continus de 6 mA (chapitre 7.22).',
        en: 'Each EV charging point has its own circuit and its own RCD of at most 30 mA: type B, or type A with 6 mA DC fault detection (chapter 7.22).',
        nl: 'Elk laadpunt heeft een eigen kring en een eigen differentieel van hoogstens 30 mA: type B, of type A met detectie van 6 mA gelijkstroom (hoofdstuk 7.22).',
      },
      fix: {
        fr: 'Placez la borne seule sous un différentiel 30 mA (type B, ou type A avec détection 6 mA DC), ou utilisez un disjoncteur différentiel 30 mA.',
        en: 'Put the charger alone behind a 30 mA RCD (type B, or type A with 6 mA DC detection), or use a 30 mA RCBO.',
        nl: 'Plaats de laadpaal alleen achter een differentieel van 30 mA (type B, of type A met 6 mA DC-detectie), of gebruik een differentieelautomaat van 30 mA.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        if (!c.points.some((p) => p.element.symbolId === 'iec.ev-charger')) return [];
        const own = [c.device, ...c.rcds].find(
          (r) =>
            (r.symbolId === 'iec.rcbo' || r.symbolId.startsWith('iec.rcd')) &&
            sensitivityMa(r) <= 30 &&
            (r.id === c.device.id || (a.byRcd.get(r.id) ?? []).length === 1),
        );
        if (own && !['AC', 'F'].includes(rcdType(own))) return [];
        const shared = c.rcds.find((r) => sensitivityMa(r) <= 30);
        const detail = own
          ? { fr: `type ${rcdType(own)}`, en: `type ${rcdType(own)}`, nl: `type ${rcdType(own)}` }
          : shared
            ? {
                fr: `${shared.tag} partagé avec d’autres circuits`,
                en: `${shared.tag} shared with other circuits`,
                nl: `${shared.tag} gedeeld met andere kringen`,
              }
            : { fr: 'aucun 30 mA', en: 'no 30 mA RCD', nl: 'geen 30 mA' };
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} (borne de recharge) : ${detail.fr} ; différentiel 30 mA propre requis (type B, ou A + 6 mA DC)`,
              en: `Circuit ${name(c)} (EV charger): ${detail.en}; its own 30 mA RCD is required (type B, or A + 6 mA DC)`,
              nl: `Kring ${name(c)} (laadpaal): ${detail.nl}; eigen differentieel 30 mA vereist (type B, of A + 6 mA DC)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-CIR-01',
      severity: 'error',
      title: {
        fr: 'Plus de 8 points sur un circuit',
        en: 'More than 8 points on a circuit',
        nl: 'Meer dan 8 punten op een kring',
      },
      why: {
        fr: 'Un circuit de prises ou d’éclairage ne peut alimenter plus de 8 points (une boîte multiple compte pour un).',
        en: 'A socket or lighting circuit may feed at most 8 points (a multiple box counts as one).',
        nl: 'Een kring voor stopcontacten of verlichting mag hoogstens 8 punten voeden (een meervoudige doos telt als één).',
      },
      fix: {
        fr: 'Scindez le circuit en deux.',
        en: 'Split the circuit in two.',
        nl: 'Splits de kring in twee.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const n = c.points.filter((p) => isCountedPoint(p.kind)).length;
        return n > 8
          ? [
              {
                targets: ids(c),
                sheetId: c.sheet.id,
                message: {
                  fr: `Circuit ${name(c)} : ${n} points (max. 8)`,
                  en: `Circuit ${name(c)}: ${n} points (max. 8)`,
                  nl: `Kring ${name(c)}: ${n} punten (max. 8)`,
                },
              },
            ]
          : [];
      }),
  ),
  rule(
    {
      id: 'R-CIR-02',
      severity: 'error',
      title: {
        fr: 'Calibre trop élevé pour la section',
        en: 'Rating too high for the cable section',
        nl: 'Nominale stroom te hoog voor de doorsnede',
      },
      why: {
        fr: 'Le calibre de la protection doit être adapté à la section des conducteurs.',
        en: 'The protection rating must suit the conductor section.',
        nl: 'De nominale stroom van de beveiliging moet passen bij de doorsnede van de geleiders.',
      },
      fix: {
        fr: 'Réduisez le calibre ou augmentez la section.',
        en: 'Lower the rating or increase the section.',
        nl: 'Verlaag de nominale stroom of verhoog de doorsnede.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const max = MAX_RATING_A[String(c.cable.sectionMm2)];
        if (!max || c.ratingA === undefined || c.ratingA <= max) return [];
        const s = String(c.cable.sectionMm2).replace('.', ',');
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${c.ratingA} A sur ${s} mm² (max. ${max} A)`,
              en: `Circuit ${name(c)}: ${c.ratingA} A on ${s} mm² (max. ${max} A)`,
              nl: `Kring ${name(c)}: ${c.ratingA} A op ${s} mm² (max. ${max} A)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-CIR-03',
      severity: 'error',
      title: {
        fr: 'Appareil sans circuit dédié',
        en: 'Appliance without a dedicated circuit',
        nl: 'Toestel zonder eigen kring',
      },
      why: {
        fr: 'Les gros appareils fixes (cuisson, lave-linge, chauffe-eau, borne VE…) doivent avoir leur propre circuit.',
        en: 'Large fixed appliances (cooking, washing machine, water heater, EV charger…) need their own circuit.',
        nl: 'Grote vaste toestellen (koken, wasmachine, boiler, laadpaal…) vereisen een eigen kring.',
      },
      fix: {
        fr: 'Placez l’appareil seul sur un circuit.',
        en: 'Put the appliance alone on a circuit.',
        nl: 'Plaats het toestel alleen op een kring.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const big = c.points.filter((p) => DEDICATED.has(p.element.symbolId));
        if (!big.length || c.points.length === 1) return [];
        return [
          {
            targets: [c.device.id, ...big.map((p) => p.element.id)],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${big.map((p) => p.element.tag).join(', ')} partage le circuit`,
              en: `Circuit ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} shares the circuit`,
              nl: `Kring ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} deelt de kring`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-CIR-05',
      severity: 'error',
      title: {
        fr: 'Prise sans contact de terre',
        en: 'Socket without earth contact',
        nl: 'Stopcontact zonder aardingscontact',
      },
      why: {
        fr: 'Les prises de courant doivent comporter un contact de terre (art. 49, 86).',
        en: 'Socket outlets must have an earth contact (art. 49, 86).',
        nl: 'Stopcontacten moeten een aardingscontact hebben (art. 49, 86).',
      },
      fix: {
        fr: 'Remplacez par une prise avec terre.',
        en: 'Replace with an earthed socket.',
        nl: 'Vervang door een geaard stopcontact.',
      },
      verified: true,
    },
    ({ project }) =>
      Object.values(project.elements)
        .filter((e) => NO_EARTH.has(e.symbolId) && project.sheets[e.sheetId]?.kind === 'single-line')
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag || 'Prise'} : sans terre`,
            en: `${e.tag || 'Socket'}: no earth`,
            nl: `${e.tag || 'Stopcontact'}: geen aarding`,
          },
        })),
  ),
  rule(
    {
      id: 'R-CIR-08',
      severity: 'error',
      title: {
        fr: 'Circuit de prises en moins de 2,5 mm²',
        en: 'Socket circuit below 2.5 mm²',
        nl: 'Stopcontactkring kleiner dan 2,5 mm²',
      },
      why: {
        fr: 'Les circuits de prises de courant sont câblés en 2,5 mm² au moins ; le 1,5 mm² est réservé aux circuits d’éclairage sans prises. Valeur à confirmer dans le texte du RGIE.',
        en: 'Socket circuits are wired in at least 2.5 mm²; 1.5 mm² is for lighting circuits without sockets. To be confirmed against the RGIE text.',
        nl: 'Stopcontactkringen worden bekabeld in minstens 2,5 mm²; 1,5 mm² is voorbehouden aan verlichtingskringen zonder stopcontacten. Te bevestigen in de tekst van het AREI.',
      },
      fix: {
        fr: 'Câblez le circuit en 2,5 mm² (protection de 20 A au plus), ou placez les prises sur un circuit en 2,5 mm².',
        en: 'Wire the circuit in 2.5 mm² (protection of at most 20 A), or move the sockets to a 2.5 mm² circuit.',
        nl: 'Bekabel de kring in 2,5 mm² (beveiliging van hoogstens 20 A), of plaats de stopcontacten op een kring in 2,5 mm².',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const mm2 = c.cable.sectionMm2;
        if (!mm2 || mm2 >= 2.5 || !c.points.some((p) => p.kind === 'socket')) return [];
        const s = String(mm2).replace('.', ',');
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : prises sur ${s} mm² (min. 2,5 mm²)`,
              en: `Circuit ${name(c)}: sockets on ${mm2} mm² (min. 2.5 mm²)`,
              nl: `Kring ${name(c)}: stopcontacten op ${s} mm² (min. 2,5 mm²)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-EARTH-01',
      severity: 'warning',
      title: { fr: 'Prise de terre', en: 'Earth electrode', nl: 'Aardverbinding' },
      why: {
        fr: 'La prise de terre et sa résistance de dispersion (< 30 Ω) doivent figurer au schéma.',
        en: 'The earth electrode and its resistance (< 30 Ω) must appear on the diagram.',
        nl: 'De aardverbinding en haar spreidingsweerstand (< 30 Ω) moeten op het schema staan.',
      },
      fix: {
        fr: 'Sans prise de terre au schéma : bouton « Ajouter la prise de terre » ci-dessous, ou cherchez « terre » dans la palette. Saisissez ensuite la valeur mesurée (Contrôle › Mesures et essais). Au-delà de 30 Ω : améliorez la prise de terre (piquet supplémentaire, connexions) et mesurez à nouveau.',
        en: 'With no electrode on the diagram: the “Add the earth electrode” button below, or search “earth” in the palette. Then enter the measured value (Inspection › Test results). Above 30 Ω: improve the electrode (another rod, its connections) and measure again.',
        nl: 'Zonder aardverbinding op het schema: de knop “Aardverbinding toevoegen” hieronder, of zoek “aard” in het palet. Vul daarna de gemeten waarde in (Keuring › Metingen en testen). Boven 30 Ω: verbeter de aardverbinding (extra aardpen, verbindingen) en meet opnieuw.',
      },
      verified: false,
    },
    ({ project }) => {
      const e = Object.values(project.elements).filter((x) => x.symbolId === 'iec.earth-electrode');
      if (!e.length)
        return [
          {
            targets: [],
            message: {
              fr: 'Aucune prise de terre au schéma',
              en: 'No earth electrode on the diagram',
              nl: 'Geen aardverbinding op het schema',
            },
          },
        ];
      return e
        .filter(
          (x) =>
            x.props.resistanceOhm === undefined ||
            x.props.resistanceOhm === '' ||
            Number(x.props.resistanceOhm) > 30,
        )
        .map((x) => ({
          targets: [x.id],
          sheetId: x.sheetId,
          message:
            x.props.resistanceOhm === undefined || x.props.resistanceOhm === ''
              ? {
                  fr: 'Résistance de dispersion non indiquée',
                  en: 'Earth resistance not stated',
                  nl: 'Spreidingsweerstand niet vermeld',
                }
              : {
                  fr: `Résistance de dispersion ${x.props.resistanceOhm} Ω (> 30 Ω)`,
                  en: `Earth resistance ${x.props.resistanceOhm} Ω (> 30 Ω)`,
                  nl: `Spreidingsweerstand ${x.props.resistanceOhm} Ω (> 30 Ω)`,
                },
        }));
    },
  ),
  rule(
    {
      id: 'R-LEG-01',
      severity: 'info',
      title: { fr: 'Partie ancienne', en: 'Legacy part', nl: 'Oud gedeelte' },
      why: {
        fr: 'Les circuits antérieurs au 1er octobre 1981 sont marqués « partie ancienne » (PA). Dérogations (AREI 8.2.1) : schémas simplifiés admis, correspondance schéma ↔ plan non exigée ; restent exigés notamment le différentiel 30 mA des pièces humides et lave-linge/sèche-linge, l’interdiction du type AC et des fusibles à vis type D.',
        en: 'Circuits older than 1 October 1981 are marked “partie ancienne” (PA). Derogations (AREI 8.2.1): simplified diagrams allowed, diagram ↔ plan correspondence not required; still required include the 30 mA RCD for wet rooms and washing/drying machines, and no type AC RCDs or type D screw fuses.',
        nl: 'Kringen van vóór 1 oktober 1981 worden aangeduid als oud gedeelte. Afwijkingen (AREI 8.2.1): vereenvoudigde schema’s toegelaten, overeenkomst schema ↔ plan niet vereist; nog steeds vereist zijn onder meer de differentieel van 30 mA voor vochtige ruimtes en was- en droogtoestellen, en geen differentiëlen type AC of schroefzekeringen type D.',
      },
      fix: {
        fr: 'Vérifiez que le marquage est correct.',
        en: 'Check that the marking is right.',
        nl: 'Controleer of de aanduiding juist is.',
      },
      verified: true,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => c.circuit?.legacy)
        .map((c) => ({
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : partie ancienne`,
            en: `Circuit ${name(c)}: legacy part`,
            nl: `Kring ${name(c)}: oud gedeelte`,
          },
        })),
  ),
]);

/** The RGIE pack: Belgian rules and the shared ones, in the order findings are listed. */
export const RGIE_RULES: Rule[] = [
  'R-DOC-02',
  'R-RCD-00',
  'R-RCD-01',
  'R-RCD-02',
  'R-RCD-03',
  'R-RCD-04',
  'R-RCD-05',
  'R-CIR-01',
  'R-CIR-02',
  'R-CIR-08',
  'R-CIR-03',
  'R-CIR-04',
  'R-DEV-01',
  'R-CIR-05',
  'R-CIR-06',
  'R-CIR-07',
  'R-DOC-01',
  'R-DOC-03',
  'R-TOPO-01',
  'R-TOPO-02',
  'R-EARTH-01',
  'R-LEG-01',
  'R-PLAN-01',
  'R-CTL-01',
  'R-CTL-02',
  'R-CTL-03',
  'R-DOC-04',
  'R-CALC-01',
  'R-CALC-02',
  'R-CALC-03',
  'R-CALC-04',
  'R-CALC-05',
  'R-CALC-00',
  'R-ML-01',
  'R-SRV-01',
].map((id) => (BE[id] ?? COMMON[id])!);
